/**
 * Theme settings — local-only previews for accent color and header treatment.
 * Defaults to green (#0BBF43). Does not write Shopify theme settings.
 */

const STORAGE_KEY = 'pa-dev-style-lab';
const THEME_VERSION = 9;
const MAX_SAVED_COLORS = 12;
const ROOT = document.documentElement;
const GOOGLE_FONTS_LINK_ID = 'pa-dev-style-lab-fonts';

/** @typedef {'inherit' | 'white' | 'filled' | 'filled-bar' | 'filled-nav'} HeaderTreatment */

/**
 * @typedef {Object} ColorTokens
 * @property {string} background
 * @property {string} foreground
 * @property {string} color1
 * @property {string} color2
 * @property {string} primary
 * @property {string} primaryText
 */

/**
 * @typedef {Object} StyleLabState
 * @property {string} colorId
 * @property {HeaderTreatment} header
 * @property {boolean} [collapsed]
 * @property {string[]} savedColors
 */

const HEADER_IDS = new Set(['inherit', 'white', 'filled', 'filled-bar', 'filled-nav']);

/**
 * Accent swatches keep the published page colors and only change primary.
 * `current` always tracks the live theme accent.
 * @type {{ id: string, label: string, hex?: string }[]}
 */
const ACCENT_SWATCHES = [
  { id: 'current', label: 'Theme' },
  { id: 'green', label: 'Green', hex: '#0BBF43' },
  { id: 'ink', label: 'Ink', hex: '#17191C' },
  { id: 'steel', label: 'Steel', hex: '#5C6670' },
  { id: 'orange', label: 'Orange', hex: '#E65C00' },
  { id: 'navy', label: 'Navy', hex: '#1D4E89' },
  { id: 'teal', label: 'Teal', hex: '#0F766E' },
];

const COLOR_OVERRIDE_KEYS = [
  '--color-background',
  '--color-background-rgb',
  '--color-foreground',
  '--color-foreground-rgb',
  '--page-background',
  '--page-background-rgb',
  '--page-foreground',
  '--page-foreground-rgb',
  '--color-border',
  '--color-border-rgb',
  '--color-primary-button-text',
  '--color-primary-button-background',
  '--color-primary-button-border',
  '--color-primary-button-focus-outline',
  '--color-primary-button-hover-text',
  '--color-primary-button-hover-background',
  '--color-primary-button-hover-border',
  '--color-secondary-button-text',
  '--color-secondary-button-background',
  '--color-secondary-button-border',
  '--color-secondary-button-hover-text',
  '--color-secondary-button-hover-background',
  '--color-secondary-button-hover-border',
  '--color-input-background',
  '--color-input-text',
  '--color-input-text-rgb',
  '--color-input-border',
  '--color-variant-background',
  '--color-variant-border',
  '--color-variant-text',
  '--color-variant-text-rgb',
  '--color-variant-hover-background',
  '--color-variant-hover-text',
  '--color-variant-hover-border',
  '--color-selected-variant-background',
  '--color-selected-variant-border',
  '--color-selected-variant-text',
  '--color-selected-variant-text-rgb',
  '--color-selected-variant-hover-background',
  '--color-selected-variant-hover-text',
  '--color-selected-variant-hover-border',
  '--color-drawer-shadow-rgb',
  '--color-popover-shadow-rgb',
];

const LEGACY_OVERRIDE_KEYS = [
  '--theme-radius',
  '--style-border-radius-xs',
  '--style-border-radius-sm',
  '--style-border-radius-md',
  '--style-border-radius-lg',
  '--style-border-radius-xl',
  '--style-border-radius-inputs',
  '--style-border-radius-buttons-primary',
  '--style-border-radius-buttons-secondary',
  '--style-border-radius-popover',
  '--font-body--family',
  '--font-subheading--family',
  '--font-heading--family',
  '--font-accent--family',
  '--font-paragraph--family',
  '--font-h1--family',
  '--font-h2--family',
  '--font-h3--family',
  '--font-h4--family',
  '--font-h5--family',
  '--font-h6--family',
  '--font-h1--weight',
  '--font-h2--weight',
  '--font-h3--weight',
  '--font-h4--weight',
  '--font-h5--weight',
  '--font-h6--weight',
  '--font-heading--weight',
  '--font-subheading--weight',
  '--button-font-family-primary',
  '--button-font-family-secondary',
  '--cart-primary-font-family',
  '--cart-secondary-font-family',
];

const HEADER_KEYS = [
  '--pa-header-surface',
  '--pa-header-surface-stuck',
  '--pa-header-foreground',
  '--pa-header-border',
  '--pa-header-accent',
  '--pa-header-search-surface',
  '--pa-header-search-foreground',
  '--pa-header-badge-bg',
  '--pa-header-badge-fg',
  '--pa-header-nav-surface',
  '--pa-header-nav-surface-stuck',
  '--pa-header-nav-foreground',
  '--pa-header-nav-border',
  '--pa-header-nav-underline',
  '--pa-header-nav-ripple',
];

/**
 * @param {string} hex
 * @returns {string}
 */
function hexToRgbChannels(hex) {
  const normalized = hex.replace('#', '');
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized;
  const int = Number.parseInt(full, 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r} ${g} ${b}`;
}

/**
 * @param {string} hex
 * @returns {[number, number, number]}
 */
function hexToRgb(hex) {
  return /** @type {[number, number, number]} */ (
    hexToRgbChannels(hex).split(' ').map((n) => Number(n))
  );
}

/**
 * @param {string} hex
 * @returns {number}
 */
function brightness(hex) {
  const [r, g, b] = hexToRgb(hex);
  return (r * 299 + g * 587 + b * 114) / 1000;
}

/**
 * @param {string} a
 * @param {string} b
 * @param {number} amount
 */
function mixHex(a, b, amount) {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const t = Math.max(0, Math.min(1, amount));
  const channels = [
    Math.round(ar + (br - ar) * t),
    Math.round(ag + (bg - ag) * t),
    Math.round(ab + (bb - ab) * t),
  ];
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * @param {string} hex
 * @param {number} amount
 */
function darkenHex(hex, amount) {
  return mixHex(hex, '#000000', amount);
}

/**
 * @param {string} color
 * @returns {string | null}
 */
function toHex(color) {
  const value = color.trim();
  const hashed =
    /^#/.test(value)
      ? value
      : /^[0-9a-fA-F]{3}$/.test(value) ||
          /^[0-9a-fA-F]{6}$/.test(value) ||
          /^[0-9a-fA-F]{8}$/.test(value)
        ? `#${value}`
        : value;

  if (/^#[0-9a-fA-F]{3}$/.test(hashed)) {
    return `#${hashed
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('')}`.toUpperCase();
  }
  if (/^#[0-9a-fA-F]{6}$/.test(hashed) || /^#[0-9a-fA-F]{8}$/.test(hashed)) {
    return hashed.slice(0, 7).toUpperCase();
  }

  const rgbMatch = value.match(/rgba?\(\s*([0-9.]+)[,\s]+([0-9.]+)[,\s]+([0-9.]+)/i);
  if (!rgbMatch) return null;
  const channels = [rgbMatch[1], rgbMatch[2], rgbMatch[3]].map((c) =>
    Math.max(0, Math.min(255, Math.round(Number(c))))
  );
  return `#${channels.map((c) => c.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

/**
 * @param {string} hex
 */
function isNamedSwatchHex(hex) {
  const upper = hex.toUpperCase();
  return ACCENT_SWATCHES.some(
    (swatch) => swatch.id !== 'current' && (swatch.hex || '').toUpperCase() === upper
  );
}

/**
 * @param {unknown} value
 * @returns {string[]}
 */
function normalizeSavedColors(value) {
  if (!Array.isArray(value)) return [];
  const seen = new Set();
  const result = [];
  for (const item of value) {
    const hex = toHex(String(item || ''));
    if (!hex || seen.has(hex) || isNamedSwatchHex(hex)) continue;
    seen.add(hex);
    result.push(hex);
    if (result.length >= MAX_SAVED_COLORS) break;
  }
  return result;
}

/**
 * @param {StyleLabState} state
 * @param {string} hex
 * @returns {string[]}
 */
function withSavedColor(state, hex) {
  const next = hex.toUpperCase();
  if (!toHex(next) || isNamedSwatchHex(next)) return state.savedColors;
  const rest = state.savedColors.filter((item) => item.toUpperCase() !== next);
  const saved = [...rest, next];
  if (saved.length > MAX_SAVED_COLORS) saved.shift();
  return saved;
}

/**
 * @param {string} hex
 */
function contrastText(hex) {
  return brightness(hex) > 170 ? '#111111' : '#FFFFFF';
}

/**
 * @param {number} hue 0–360
 * @param {number} sat 0–100
 * @param {number} light 0–100
 */
function hslToHex(hue, sat, light) {
  const s = sat / 100;
  const l = light / 100;
  const a = s * Math.min(l, 1 - l);
  const channel = (n) => {
    const k = (n + hue / 30) % 12;
    const value = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * value)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${channel(0)}${channel(8)}${channel(4)}`.toUpperCase();
}

/**
 * Vivid accent that still works as a primary button fill.
 * @param {string} [avoidHex]
 */
function randomAccentHex(avoidHex) {
  let hex = '';
  for (let i = 0; i < 8; i += 1) {
    hex = hslToHex(
      Math.floor(Math.random() * 360),
      58 + Math.random() * 28,
      36 + Math.random() * 18
    );
    if (!avoidHex || hex !== avoidHex.toUpperCase()) break;
  }
  return hex;
}

/**
 * @param {HTMLElement} root
 * @returns {ColorTokens}
 */
function readThemeCoreTokens(root) {
  const styles = getComputedStyle(ROOT);
  const background =
    toHex(root.dataset.themeBackground || '') ||
    toHex(styles.getPropertyValue('--color-background')) ||
    '#FFFFFF';
  const foreground =
    toHex(root.dataset.themeForeground || '') ||
    toHex(styles.getPropertyValue('--color-foreground')) ||
    '#17191C';
  const primary =
    toHex(root.dataset.themePrimary || '') ||
    toHex(styles.getPropertyValue('--color-primary-button-background')) ||
    '#0BBF43';

  return {
    background,
    foreground,
    color1: mixHex(foreground, background, 0.35),
    color2: mixHex(foreground, background, 0.82),
    primary,
    primaryText: contrastText(primary),
  };
}

/**
 * @param {Record<string, string>} values
 */
function applyRootVars(values) {
  for (const [key, value] of Object.entries(values)) {
    if (value) ROOT.style.setProperty(key, value);
    else ROOT.style.removeProperty(key);
  }
}

/**
 * @param {string[]} keys
 */
function clearVars(keys) {
  for (const key of keys) ROOT.style.removeProperty(key);
}

function clearLegacyOverrides() {
  clearVars(LEGACY_OVERRIDE_KEYS);
  document.getElementById(GOOGLE_FONTS_LINK_ID)?.remove();
}

/**
 * @param {ColorTokens} tokens
 */
function applyColorTokens(tokens) {
  const bgRgb = hexToRgbChannels(tokens.background);
  const fgRgb = hexToRgbChannels(tokens.foreground);
  const primaryHover = darkenHex(tokens.primary, 0.12);
  const variantHoverBg = mixHex(tokens.background, tokens.foreground, 0.06);
  const selectedHoverBg = darkenHex(tokens.foreground, 0.08);

  applyRootVars({
    '--color-background': tokens.background,
    '--color-background-rgb': bgRgb,
    '--color-foreground': tokens.foreground,
    '--color-foreground-rgb': fgRgb,
    '--page-background': tokens.background,
    '--page-background-rgb': bgRgb,
    '--page-foreground': tokens.foreground,
    '--page-foreground-rgb': fgRgb,
    '--color-border': tokens.foreground,
    '--color-border-rgb': fgRgb,
    '--color-primary-button-text': tokens.primaryText,
    '--color-primary-button-background': tokens.primary,
    '--color-primary-button-border': tokens.primary,
    '--color-primary-button-focus-outline': tokens.primary,
    '--color-primary-button-hover-text': tokens.primaryText,
    '--color-primary-button-hover-background': primaryHover,
    '--color-primary-button-hover-border': primaryHover,
    '--color-secondary-button-text': tokens.foreground,
    '--color-secondary-button-background': 'transparent',
    '--color-secondary-button-border': tokens.foreground,
    '--color-secondary-button-hover-text': tokens.foreground,
    '--color-secondary-button-hover-background': `rgb(${fgRgb} / 0.06)`,
    '--color-secondary-button-hover-border': tokens.foreground,
    '--color-input-background': tokens.background,
    '--color-input-text': tokens.color1,
    '--color-input-text-rgb': hexToRgbChannels(tokens.color1),
    '--color-input-border': tokens.color2,
    '--color-variant-background': tokens.background,
    '--color-variant-border': tokens.color2,
    '--color-variant-text': tokens.foreground,
    '--color-variant-text-rgb': fgRgb,
    '--color-variant-hover-background': variantHoverBg,
    '--color-variant-hover-text': tokens.foreground,
    '--color-variant-hover-border': tokens.color1,
    '--color-selected-variant-background': tokens.foreground,
    '--color-selected-variant-border': tokens.foreground,
    '--color-selected-variant-text': tokens.background,
    '--color-selected-variant-text-rgb': bgRgb,
    '--color-selected-variant-hover-background': selectedHoverBg,
    '--color-selected-variant-hover-text': tokens.background,
    '--color-selected-variant-hover-border': selectedHoverBg,
    '--color-drawer-shadow-rgb': fgRgb,
    '--color-popover-shadow-rgb': fgRgb,
  });
}

/**
 * @param {string} accentHex
 * @param {string} pageBg
 * @param {string} pageFg
 */
function applyFilledIdentity(accentHex, pageBg, pageFg) {
  const onAccent = contrastText(accentHex);
  ROOT.style.setProperty('--pa-header-surface', accentHex);
  ROOT.style.setProperty('--pa-header-surface-stuck', darkenHex(accentHex, 0.08));
  ROOT.style.setProperty('--pa-header-foreground', onAccent);
  ROOT.style.setProperty(
    '--pa-header-border',
    onAccent === '#FFFFFF' ? 'rgb(255 255 255 / 0.22)' : 'rgb(0 0 0 / 0.16)'
  );
  ROOT.style.setProperty('--pa-header-accent', accentHex);
  ROOT.style.setProperty('--pa-header-search-surface', pageBg);
  ROOT.style.setProperty('--pa-header-search-foreground', pageFg);
  ROOT.style.setProperty('--pa-header-badge-bg', onAccent);
  ROOT.style.setProperty('--pa-header-badge-fg', accentHex);
}

/**
 * @param {string} accentHex
 */
function applyFilledNav(accentHex) {
  const onAccent = contrastText(accentHex);
  ROOT.style.setProperty('--pa-header-nav-surface', accentHex);
  ROOT.style.setProperty('--pa-header-nav-surface-stuck', darkenHex(accentHex, 0.08));
  ROOT.style.setProperty('--pa-header-nav-foreground', onAccent);
  ROOT.style.setProperty(
    '--pa-header-nav-border',
    onAccent === '#FFFFFF' ? 'rgb(255 255 255 / 0.22)' : 'rgb(0 0 0 / 0.16)'
  );
  ROOT.style.setProperty('--pa-header-nav-underline', onAccent);
  ROOT.style.setProperty('--pa-header-nav-ripple', onAccent);
}

/**
 * @param {HeaderTreatment} treatment
 * @param {ColorTokens} themeCore
 * @param {string} accentHex
 */
function applyHeaderTreatment(treatment, themeCore, accentHex) {
  ROOT.setAttribute('data-style-lab-header', treatment);
  clearVars(HEADER_KEYS);

  if (treatment === 'inherit') return;

  const pageFg = themeCore.foreground;
  const pageBg = themeCore.background;

  if (treatment === 'white') {
    const surface = 'color-mix(in srgb, #ffffff 92%, transparent)';
    const stuck = 'color-mix(in srgb, #ffffff 98%, transparent)';
    const border = `color-mix(in srgb, ${pageFg} 18%, transparent)`;
    ROOT.style.setProperty('--pa-header-surface', surface);
    ROOT.style.setProperty('--pa-header-surface-stuck', stuck);
    ROOT.style.setProperty('--pa-header-foreground', pageFg);
    ROOT.style.setProperty('--pa-header-border', border);
    ROOT.style.setProperty('--pa-header-accent', accentHex);
    ROOT.style.setProperty('--pa-header-search-surface', '#FFFFFF');
    ROOT.style.setProperty('--pa-header-search-foreground', pageFg);
    ROOT.style.setProperty('--pa-header-badge-bg', accentHex);
    ROOT.style.setProperty('--pa-header-badge-fg', contrastText(accentHex));
    ROOT.style.setProperty('--pa-header-nav-surface', surface);
    ROOT.style.setProperty('--pa-header-nav-surface-stuck', stuck);
    ROOT.style.setProperty('--pa-header-nav-foreground', pageFg);
    ROOT.style.setProperty('--pa-header-nav-border', border);
    return;
  }

  if (treatment === 'filled-bar') {
    applyFilledIdentity(accentHex, pageBg, pageFg);
    return;
  }

  if (treatment === 'filled-nav') {
    applyFilledNav(accentHex);
    return;
  }

  applyFilledIdentity(accentHex, pageBg, pageFg);
  applyFilledNav(accentHex);
}

/**
 * @param {string} colorId
 * @param {ColorTokens} themeCore
 */
function resolveAccent(colorId, themeCore) {
  if (colorId === 'current' || !colorId) return themeCore.primary;
  const named = ACCENT_SWATCHES.find((swatch) => swatch.id === colorId);
  if (named?.hex) return named.hex;
  return toHex(colorId) || themeCore.primary;
}

/**
 * @returns {StyleLabState}
 */
function defaultState() {
  return { colorId: 'green', header: 'inherit', collapsed: true, savedColors: [] };
}

/**
 * @returns {StyleLabState}
 */
function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw);
    if (parsed.version < 4) return defaultState();

    let colorId =
      typeof parsed.colorId === 'string' && parsed.colorId ? parsed.colorId : 'green';
    if (parsed.version < 9 && colorId === 'current') colorId = 'green';
    const header = HEADER_IDS.has(parsed.header) ? parsed.header : 'inherit';

    return {
      colorId,
      header,
      collapsed: parsed.collapsed !== false,
      savedColors: normalizeSavedColors(parsed.savedColors),
    };
  } catch {
    return defaultState();
  }
}

/**
 * @param {StyleLabState} state
 */
function saveState(state) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: THEME_VERSION,
        colorId: state.colorId,
        header: state.header,
        collapsed: state.collapsed !== false,
        savedColors: normalizeSavedColors(state.savedColors),
      })
    );
  } catch {
    // Ignore quota / private mode failures
  }
}

/**
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 * @param {string} swatchId
 * @param {string} swatchHex
 */
function isSwatchPressed(state, themeCore, swatchId, swatchHex) {
  if (swatchId === 'current') return state.colorId === 'current';
  if (state.colorId === swatchId) return true;
  if (state.colorId === 'current') return false;
  return resolveAccent(state.colorId, themeCore).toUpperCase() === swatchHex.toUpperCase();
}

/**
 * @param {HTMLElement} root
 * @param {ColorTokens} themeCore
 */
function renderColorSwatches(root, themeCore) {
  const container = root.querySelector('[data-color-swatches]');
  if (!(container instanceof HTMLElement) || container.childElementCount) return;

  for (const swatch of ACCENT_SWATCHES) {
    const hex = (swatch.hex || themeCore.primary).toUpperCase();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dev-style-lab__dot';
    button.dataset.color = swatch.id;
    button.setAttribute('aria-label', swatch.label);
    button.setAttribute('aria-pressed', 'false');

    const fill = document.createElement('span');
    fill.className = 'dev-style-lab__dot-fill';
    fill.setAttribute('aria-hidden', 'true');
    fill.style.setProperty('--dsl-dot', hex);
    button.append(fill);
    container.append(button);
  }

  const customWrap = document.createElement('label');
  customWrap.className = 'dev-style-lab__dot dev-style-lab__dot--custom';
  customWrap.dataset.colorCustomWrap = '';

  const customFill = document.createElement('span');
  customFill.className = 'dev-style-lab__dot-fill';
  customFill.setAttribute('aria-hidden', 'true');
  customFill.dataset.colorCustomFill = '';

  const input = document.createElement('input');
  input.type = 'color';
  input.value = themeCore.primary;
  input.setAttribute('aria-label', 'Custom accent color');
  input.dataset.colorCustom = '';

  customWrap.append(customFill, input);
  container.append(customWrap);
}

/**
 * @param {string} hex
 * @returns {HTMLElement}
 */
function createSavedSwatch(hex) {
  const wrap = document.createElement('span');
  wrap.className = 'dev-style-lab__dot-wrap';
  wrap.dataset.savedWrap = '';

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'dev-style-lab__dot';
  button.dataset.color = hex;
  button.dataset.savedColor = '';
  button.setAttribute('aria-label', hex);
  button.setAttribute('aria-pressed', 'false');
  button.title = hex;

  const fill = document.createElement('span');
  fill.className = 'dev-style-lab__dot-fill';
  fill.setAttribute('aria-hidden', 'true');
  fill.style.setProperty('--dsl-dot', hex);
  button.append(fill);

  const remove = document.createElement('button');
  remove.type = 'button';
  remove.className = 'dev-style-lab__dot-remove';
  remove.dataset.colorUnsave = hex;
  remove.setAttribute('aria-label', `Remove ${hex}`);
  remove.tabIndex = -1;
  remove.innerHTML = '&times;';

  wrap.append(button, remove);
  return wrap;
}

/**
 * @param {HTMLElement} root
 * @param {StyleLabState} state
 */
function renderSavedSwatches(root, state) {
  const container = root.querySelector('[data-color-swatches]');
  const customWrap = root.querySelector('[data-color-custom-wrap]');
  if (!(container instanceof HTMLElement) || !(customWrap instanceof HTMLElement)) return;

  const key = state.savedColors.join(',');
  if (container.dataset.savedKey === key) return;
  container.dataset.savedKey = key;
  container.querySelectorAll('[data-saved-wrap]').forEach((el) => el.remove());

  for (const hex of state.savedColors) {
    container.insertBefore(createSavedSwatch(hex), customWrap);
  }
}

/**
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 */
function canSaveColor(state, themeCore) {
  if (state.colorId === 'current') return false;
  const hex = resolveAccent(state.colorId, themeCore).toUpperCase();
  if (!toHex(hex) || isNamedSwatchHex(hex)) return false;
  return !state.savedColors.some((item) => item.toUpperCase() === hex);
}

/**
 * @param {HTMLElement} root
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 */
function syncColorSwatches(root, state, themeCore) {
  const selectedHex = resolveAccent(state.colorId, themeCore).toUpperCase();
  const namedMatch = ACCENT_SWATCHES.some(
    (swatch) =>
      swatch.id !== 'current' &&
      isSwatchPressed(state, themeCore, swatch.id, swatch.hex || themeCore.primary)
  );
  const savedMatch = state.savedColors.some((item) => item.toUpperCase() === selectedHex);

  root.querySelectorAll('[data-color]').forEach((btn) => {
    if (!(btn instanceof HTMLElement) || !btn.dataset.color) return;
    const swatch = ACCENT_SWATCHES.find((item) => item.id === btn.dataset.color);
    const hex = swatch?.hex || btn.dataset.color;
    btn.setAttribute(
      'aria-pressed',
      String(isSwatchPressed(state, themeCore, btn.dataset.color, hex))
    );
  });

  const customWrap = root.querySelector('[data-color-custom-wrap]');
  const customFill = root.querySelector('[data-color-custom-fill]');
  const customInput = root.querySelector('[data-color-custom]');
  const isCustom = state.colorId !== 'current' && !namedMatch && !savedMatch;
  if (customWrap instanceof HTMLElement) {
    customWrap.classList.toggle('is-selected', isCustom);
  }
  if (customFill instanceof HTMLElement) {
    if (isCustom) customFill.style.setProperty('--dsl-dot', selectedHex);
    else customFill.style.removeProperty('--dsl-dot');
  }
  if (customInput instanceof HTMLInputElement && customInput.value.toUpperCase() !== selectedHex) {
    customInput.value = selectedHex;
  }
}

/**
 * @param {HTMLElement} root
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 */
function syncHexAndSave(root, state, themeCore) {
  const selectedHex = resolveAccent(state.colorId, themeCore).toUpperCase();
  const hexInput = root.querySelector('[data-color-hex]');
  if (hexInput instanceof HTMLInputElement && document.activeElement !== hexInput) {
    hexInput.value = selectedHex;
  }

  const save = root.querySelector('[data-color-save]');
  if (save instanceof HTMLButtonElement) {
    save.disabled = !canSaveColor(state, themeCore);
  }
}

/**
 * @param {HTMLElement} root
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 */
function syncUi(root, state, themeCore) {
  renderSavedSwatches(root, state);
  syncColorSwatches(root, state, themeCore);
  syncHexAndSave(root, state, themeCore);

  root.querySelectorAll('[data-header]').forEach((btn) => {
    if (!(btn instanceof HTMLElement)) return;
    btn.setAttribute('aria-pressed', String(btn.dataset.header === state.header));
  });

  const panel = root.querySelector('[data-style-lab-panel]');
  const toggle = root.querySelector('[data-style-lab-toggle]');
  const collapsed = state.collapsed !== false;

  root.dataset.collapsed = String(collapsed);
  if (panel instanceof HTMLElement) panel.hidden = collapsed;
  if (toggle instanceof HTMLElement) toggle.setAttribute('aria-expanded', String(!collapsed));
}

/**
 * @param {StyleLabState} state
 * @param {ColorTokens} themeCore
 */
function applyState(state, themeCore) {
  const accent = resolveAccent(state.colorId, themeCore);
  const colorChanged = state.colorId !== 'current';

  ROOT.setAttribute('data-style-lab-color', state.colorId);
  ROOT.setAttribute('data-style-lab-theme', colorChanged ? 'preview' : 'current');

  if (colorChanged) {
    applyColorTokens({
      ...themeCore,
      primary: accent,
      primaryText: contrastText(accent),
    });
  } else {
    clearVars(COLOR_OVERRIDE_KEYS);
  }

  applyHeaderTreatment(state.header, themeCore, accent);
}

/**
 * @param {HTMLElement} root
 */
function bind(root) {
  clearLegacyOverrides();
  const themeCore = readThemeCoreTokens(root);
  let state = loadState();
  renderColorSwatches(root, themeCore);

  const persistAndApply = () => {
    saveState(state);
    applyState(state, themeCore);
    syncUi(root, state, themeCore);
  };

  /**
   * @param {string} raw
   */
  const applyColorValue = (raw) => {
    const hex = toHex(raw);
    if (!hex) return false;
    const named = ACCENT_SWATCHES.find((swatch) => (swatch.hex || '').toUpperCase() === hex);
    const saved = state.savedColors.find((item) => item.toUpperCase() === hex);
    state = { ...state, colorId: named?.id || saved || hex };
    persistAndApply();
    return true;
  };

  root.querySelector('[data-style-lab-toggle]')?.addEventListener('click', () => {
    state = { ...state, collapsed: !state.collapsed };
    persistAndApply();
  });

  root.querySelector('[data-color-swatches]')?.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const unsave = target.closest('[data-color-unsave]');
    if (unsave instanceof HTMLElement && unsave.dataset.colorUnsave) {
      event.preventDefault();
      const hex = toHex(unsave.dataset.colorUnsave);
      if (!hex) return;
      state = {
        ...state,
        savedColors: state.savedColors.filter((item) => item.toUpperCase() !== hex),
      };
      persistAndApply();
      return;
    }

    if (target.closest('[data-color-custom]')) return;
    const button = target.closest('[data-color]');
    if (!(button instanceof HTMLElement) || !button.dataset.color) return;
    state = { ...state, colorId: button.dataset.color };
    persistAndApply();
  });

  root.querySelector('[data-color-swatches]')?.addEventListener('keydown', (event) => {
    if (event.key !== 'Delete') return;
    const target = event.target;
    if (!(target instanceof HTMLElement) || !target.dataset.savedColor || !target.dataset.color) {
      return;
    }
    event.preventDefault();
    const hex = toHex(target.dataset.color);
    if (!hex) return;
    state = {
      ...state,
      savedColors: state.savedColors.filter((item) => item.toUpperCase() !== hex),
    };
    persistAndApply();
  });

  root.querySelector('[data-color-swatches]')?.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.dataset.colorCustom === undefined) return;
    applyColorValue(target.value);
  });

  const hexInput = root.querySelector('[data-color-hex]');
  hexInput?.addEventListener('input', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const trimmed = target.value.trim();
    const digits = trimmed.replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(digits)) return;
    const hex = toHex(trimmed);
    if (hex && /^rgba?\(/i.test(trimmed)) target.value = hex;
    applyColorValue(target.value);
  });
  hexInput?.addEventListener('blur', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    const hex = toHex(target.value);
    target.value = hex || resolveAccent(state.colorId, themeCore).toUpperCase();
  });
  hexInput?.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    event.preventDefault();
    if (!applyColorValue(target.value)) return;
    if (!canSaveColor(state, themeCore)) return;
    state = {
      ...state,
      savedColors: withSavedColor(state, resolveAccent(state.colorId, themeCore)),
    };
    persistAndApply();
  });

  root.querySelector('[data-color-save]')?.addEventListener('click', () => {
    if (!canSaveColor(state, themeCore)) return;
    state = {
      ...state,
      savedColors: withSavedColor(state, resolveAccent(state.colorId, themeCore)),
    };
    persistAndApply();
  });

  root.querySelector('[data-color-random]')?.addEventListener('click', () => {
    const current = resolveAccent(state.colorId, themeCore);
    applyColorValue(randomAccentHex(current));
  });

  root.querySelectorAll('[data-header]').forEach((btn) => {
    btn.addEventListener('click', () => {
      if (!(btn instanceof HTMLElement) || !btn.dataset.header) return;
      state = { ...state, header: /** @type {HeaderTreatment} */ (btn.dataset.header) };
      persistAndApply();
    });
  });

  root.querySelector('[data-style-lab-reset]')?.addEventListener('click', () => {
    state = { ...defaultState(), collapsed: false, savedColors: state.savedColors };
    persistAndApply();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || state.collapsed !== false) return;
    const panel = root.querySelector('[data-style-lab-panel]');
    if (!(panel instanceof HTMLElement) || panel.hidden) return;
    if (!root.contains(document.activeElement) && document.activeElement !== document.body) {
      return;
    }
    state = { ...state, collapsed: true };
    persistAndApply();
    const toggle = root.querySelector('[data-style-lab-toggle]');
    if (toggle instanceof HTMLElement) toggle.focus();
  });

  document.addEventListener('shopify:section:load', () => {
    applyState(state, themeCore);
    syncUi(root, state, themeCore);
  });

  persistAndApply();
}

function init() {
  const root = document.querySelector('[data-dev-style-lab]');
  if (!(root instanceof HTMLElement)) return;
  bind(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
